import { memo, useCallback, useMemo } from 'react';

import { type BaseCustomField, type CustomFieldValue, type ResponseCustomFieldValue } from '@air/api/types';
import { useBatchUpdateFormAssetsMutation } from '@air/feature-content-collection/hooks';
import {
  type ContentCollectionCustomField,
  contentCollectionSessionIdSelector,
} from '@air/feature-content-collection/redux';

import {
  type ContentCollectionBaseCustomFieldsProps,
  ContentCollectionCustomFieldsSelect,
} from '~/components/ContentCollection/ContentCollectionBaseCustomFieldsSelect';
import { useAirStore } from '~/utils/ReduxUtils';

export type ContentCollectionCustomFieldSingleSelectProps = {
  customField: BaseCustomField;
  onSuccess: (params: {
    customFieldWithValues: ContentCollectionCustomField;
    values: CustomFieldValue[] | null;
  }) => void;
  value: ResponseCustomFieldValue | null | undefined;
} & Omit<ContentCollectionBaseCustomFieldsProps, 'field'>;

export const ContentCollectionCustomFieldSingleSelect = memo(
  ({
    clips,
    customField,
    disabled,
    onSuccess,
    value,
    showDescription,
  }: ContentCollectionCustomFieldSingleSelectProps) => {
    const store = useAirStore();
    const { mutate: batchUpdateFormAssets, isPending } = useBatchUpdateFormAssetsMutation();

    const customFieldWithValues = useMemo<ContentCollectionCustomField>(
      () => ({
        ...customField,
        value: value ?? null,
        values: null,
      }),
      [customField, value],
    );

    const onSelectionChange = useCallback(
      ({ values }: { values: CustomFieldValue[] | null }) => {
        const sessionId = contentCollectionSessionIdSelector(store.getState());

        if (!sessionId) {
          throw new Error('No session id');
        }

        batchUpdateFormAssets(
          {
            assetIds: clips.map(({ assetId }) => assetId),
            formSessionId: sessionId,
            update: {
              customFields: [{ customFieldId: customField.id, valueId: values?.[0]?.id }],
            },
          },
          {
            onSuccess: () => {
              onSuccess({ customFieldWithValues, values });
            },
          },
        );
      },
      [batchUpdateFormAssets, clips, customField.id, customFieldWithValues, onSuccess, store],
    );

    return (
      <ContentCollectionCustomFieldsSelect
        disabled={disabled || isPending}
        clips={clips}
        field={customFieldWithValues}
        onSelectionChange={onSelectionChange}
        showDescription={showDescription}
      />
    );
  },
);

ContentCollectionCustomFieldSingleSelect.displayName = 'ContentCollectionCustomFieldSingleSelect';
